// src/hooks/tokens/useTokensScreenerData.ts
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { TIME_WINDOWS } from "@/constants/metrics";
import {
  fetchTokensCurrent,
  fetchTokensCurrentRatios,
  fetchTokensRollups,
  fetchTokensChanges,
  type ScreenerCurrentValuesResponse,
  type ScreenerWindowsResponse,
} from "@/services/queries/tokens/tokensScreenerQueries";
import { adaptMetricsRows, adaptRatiosRows } from "@/utils/dashboard/dashboardDataAdapter";
import type { CurrentValuesResponse, TokenCurrent } from "@/types/dashboards/dashboardCurrent";

type DashboardResponse<T = Record<string, unknown>> = { data: T[]; lastUpdated?: string | undefined };

function mergeRowsByKey(primary: Array<Record<string, unknown>>, secondary: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
  const byKey = new Map<string, Record<string, unknown>>();
  const getKey = (r: Record<string, unknown>) => String(r["tokenSymbol"] || r["tokenName"] || "");
  for (const r of primary) {
    const k = getKey(r);
    byKey.set(k, { ...(byKey.get(k) || {}), ...r });
  }
  for (const r of secondary) {
    const k = getKey(r);
    byKey.set(k, { ...(byKey.get(k) || {}), ...r });
  }
  return Array.from(byKey.values());
}

export function useTokensScreenerData() {
  const windowsCsv = useMemo(() => TIME_WINDOWS.API.join(","), []);

  const currentQuery = useQuery<ScreenerCurrentValuesResponse>({
    queryKey: ["tokens", "screener", "current"],
    queryFn: ({ signal }) => fetchTokensCurrent({ sortOrder: "desc", limit: 1000, signal }),
    staleTime: 60_000,
  });

  const ratiosQuery = useQuery<ScreenerCurrentValuesResponse>({
    queryKey: ["tokens", "screener", "ratios"],
    queryFn: ({ signal }) => fetchTokensCurrentRatios({ sortOrder: "desc", limit: 1000, signal }),
    staleTime: 60_000,
  });

  const rollupsQuery = useQuery<ScreenerWindowsResponse>({
    queryKey: ["tokens", "screener", "rollups", windowsCsv],
    queryFn: ({ signal }) => fetchTokensRollups({ windowsCsv, sortOrder: "desc", limit: 1000, signal }),
    staleTime: 60_000,
  });

  const changesQuery = useQuery<ScreenerWindowsResponse>({
    queryKey: ["tokens", "screener", "changes", windowsCsv],
    queryFn: ({ signal }) => fetchTokensChanges({ windowsCsv, sortOrder: "desc", limit: 1000, signal }),
    staleTime: 60_000,
  });

  const merged = useMemo<DashboardResponse>(() => {
    const toCurrentResponse = (list: ScreenerCurrentValuesResponse["tokens"]): CurrentValuesResponse => {
      const sanitized: TokenCurrent[] = list.map((t) => {
        const obj: TokenCurrent = {
          token_id: t.token_id,
          ticker: t.ticker,
          metrics: t.metrics,
        };
        if (t.name != null) obj.name = t.name;
        if (t.color != null) obj.color = t.color;
        if (t.logo_url != null) obj.logo_url = t.logo_url;
        return obj;
      });
      return { category_id: -1, tokens: sanitized };
    };

    const currentRows = currentQuery.data ? adaptMetricsRows(toCurrentResponse(currentQuery.data.tokens), { category_id: -1, tokens: [] }) : [];
    const ratiosRows = ratiosQuery.data ? adaptRatiosRows(toCurrentResponse(ratiosQuery.data.tokens)) : [];
    // rollups and changes are optional for the current table; available for presets later
    const rows = mergeRowsByKey(currentRows as Record<string, unknown>[], ratiosRows as Record<string, unknown>[]);
    return { data: rows, lastUpdated: currentQuery.data?.lastUpdated };
  }, [currentQuery.data, ratiosQuery.data]);

  return {
    data: merged.data,
    lastUpdated: merged.lastUpdated,
    isLoading: currentQuery.isLoading || ratiosQuery.isLoading,
    isFetching: currentQuery.isFetching || ratiosQuery.isFetching,
    isError: currentQuery.isError || ratiosQuery.isError,
    error: currentQuery.error || ratiosQuery.error,
    refetch: () => {
      currentQuery.refetch();
      ratiosQuery.refetch();
      rollupsQuery.refetch();
      changesQuery.refetch();
    },
  };
}


